import { trimEnd } from 'lodash';
import { intl } from '@fullstory/intl';

const MAX_CARD_LENGTH = 19;

export function bestDisplayNameforUser(sourceDisplayName: string, email: string): string {
  let displayName = sourceDisplayName;
  const nameIsBlank = !displayName || displayName.trim() == '';
  if ((nameIsBlank || displayName.indexOf('User ') == 0) && email && email.trim() != '') {
    // If the display name is blank or the default string and email is set, fall back to email.
    displayName = email.trim();
  } else if (nameIsBlank) {
    displayName = intl.formatMessage({
      defaultMessage: '(No name available)',
      id: 'aXPNew',
      description: 'Name refers to a display name for a user.',
    });
  }

  return displayName;
}

export function makeAnchor(
  text: string,
  link: string,
  className: string,
  click: () => void,
): HTMLAnchorElement {
  return makeAnchorWithTitle(link, text, className, '', click);
}

export function makeAnchorWithTitle(
  link: string,
  text: string,
  className: string,
  titleText: string,
  click: () => void,
): HTMLAnchorElement {
  const a = document.createElement('a');
  jsAnchorWithTitle(a, link, click, titleText, text, className);
  return a;
}

// Makes an existing anchor element have an href, yet do some JS action on click instead of native navigation when the left
// mouse button is clicked. Middle clicks and right clicks for example should do the native behavior.
export function jsAnchorWithTitle(
  a: HTMLAnchorElement,
  link: string,
  click: (e?: MouseEvent) => void,
  titleText?: string,
  text?: string,
  className?: string,
) {
  if (text) {
    a.textContent = text;
  }
  if (className) {
    a.className = className;
  }
  if (titleText) {
    a.setAttribute('title', titleText);
  }
  a.href = link;
  a.onclick = (e: MouseEvent) => {
    if (e.button == 0 && !e.ctrlKey && !e.metaKey) {
      // Fire/prevent-default action only if the user is left-clicking without ctrl/cmd held.
      e.preventDefault();
      click(e);
    }
  };
}

export function confirm(msg: string, confirmationTxt: string, cb: () => void) {
  if (
    window.prompt(`${msg} \n\n To proceed, enter the text '${confirmationTxt}'.`) == confirmationTxt
  ) {
    cb();
  } else {
    window.alert('No worries. Doing nothing.');
  }
}

type CardType = 'visa' | 'amex' | 'mastercard' | 'discover' | 'diners' | 'unknown';

const knownCards = new Map<string, CardType>([
  ['visa', 'visa'],
  ['amex', 'amex'],
  ['mastercard', 'mastercard'],
  ['american express', 'amex'],
  ['discover', 'discover'],
  ['diners', 'diners'],
  ['diners club', 'diners'],
]);

export function cardType(cardName: string): CardType {
  return knownCards.get(cardName.toLowerCase()) ?? 'unknown';
}

// Formats user-entered and "last four only" card numbers with spaces.
export function formatCardNumber(inputValue: string): string {
  // strip spaces and invalid chars
  const numbersOnly = inputValue.replace(/[^0-9*]/g, '').slice(0, MAX_CARD_LENGTH);
  let spaced = '';
  let i = 0;
  while (i + 4 < numbersOnly.length) {
    spaced += `${numbersOnly.substr(i, 4)} `;
    i += 4;
  }
  spaced += numbersOnly.substr(i);
  return spaced;
}

export function loadFromLocalStorage(name: string, defaultIfNotPresent: string): string {
  try {
    return localStorage?.getItem(name) ?? defaultIfNotPresent;
  } catch (_) {
    // Firefox throws in embed context.
    return defaultIfNotPresent;
  }
}

export function saveToLocalStorage(name: string, val: string) {
  try {
    localStorage?.setItem(name, val);
  } catch (e) {
    // Safari throws over-quota error in private-browsing mode
    // Firefox sometimes throws even just accessing the window.localStorage property.
  }
}

let sharedCanvas: HTMLCanvasElement | null;
let _sharedCanvasCtx: CanvasRenderingContext2D | null;
function sharedCanvasCtx(): CanvasRenderingContext2D {
  if (!_sharedCanvasCtx) {
    sharedCanvas = document.createElement('canvas');
    _sharedCanvasCtx = sharedCanvas.getContext('2d');
  }
  return <CanvasRenderingContext2D>_sharedCanvasCtx;
}

export function measureString(string: string, font?: string): number {
  // font object value is size and family '12px Arial'
  const fontToMeasure = font
    ? font
    : '12px "Whitney SSm A", "Whitney SSm B", "Helvetica Neue", Arial, sans-serif';
  sharedCanvasCtx().font = fontToMeasure;
  const stringMeasurement = Number(sharedCanvasCtx().measureText(string).width.toFixed());

  return stringMeasurement;
}

// cooldown period after function is cast.
// Blocks subsequent calls to function. Useful if you want an arbitrary blocking period.
// I.e. async actions that are at the will of the Browser, outside of our sandbox. (window.assign)
// Returns the result of spell, or defVal if spell was blocked.
export function cooldown<T>(spell: () => T, ms: number, defVal: T) {
  let blocking = false;
  return (): T => {
    if (blocking) {
      return defVal;
    }
    blocking = true;
    setTimeout(() => {
      blocking = false;
    }, ms);
    return spell();
  };
}

// Link utils
export function hrefForUserCard(pathname: string, indivId: string, search?: string): string {
  const trimmed = trimEnd(pathname, '/');
  const appendedUserPath = `/user/${indivId}`;

  // if pathname ends with /user/*, swap out the indivId (may be the same)
  if (/\/user\/[0-9]+$/.test(pathname)) {
    return pathname.replace(/\/user\/[0-9]+$/, appendedUserPath);
  }

  return `${trimmed}${appendedUserPath}${search ?? ''}`;
}

export const removeTrailing = (input: string, char: string = '/'): string => {
  if (input && input.length && input[input.length - 1] == char) {
    return input.substring(0, input.length - 1);
  }
  return input;
};
